import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, Switch, Flex } from '@chakra-ui/react';

interface PastPerformanceStylerProps {
  containerSelector?: string;
  venueSelector?: string;
  timeSelector?: string;
  dateFormat?: 'US' | 'ISO'; // US = MM/DD/YYYY or ISO = YYYY-MM-DD
}

interface PerformanceItem {
  element: HTMLElement;
  time: string;
  date?: string;
  venue: string;
  isPast: boolean;
}

const PastPerformanceStyler: React.FC<PastPerformanceStylerProps> = ({
  containerSelector = 'details',
  venueSelector = 'summary',
  timeSelector = 'ul li',
  dateFormat = 'US',
}) => {
  const [performances, setPerformances] = useState<PerformanceItem[]>([]);
  const [showPastPerformances, setShowPastPerformances] = useState<boolean>(true);
  const [today] = useState<Date>(new Date());
  const observerRef = useRef<MutationObserver | null>(null);
  const initializationAttempts = useRef<number>(0);
  
  // Initialize performance items when the component mounts or when the DOM changes
  const initializePerformances = () => {
    const containers = document.querySelectorAll(containerSelector);
    
    // If no containers are found, we might need to try again later
    if (containers.length === 0) {
      const maxAttempts = 10;
      if (initializationAttempts.current < maxAttempts) {
        initializationAttempts.current += 1;
        // Retry after a short delay
        setTimeout(() => {
          initializePerformances();
        }, 200);
      }
      return;
    }
    
    const items: PerformanceItem[] = [];
    
    containers.forEach((container) => {
      if (container instanceof HTMLElement) {
        const summaryElement = container.querySelector(venueSelector);
        const listElements = container.querySelectorAll(timeSelector);
        
        // Extract venue name
        let venueName = '';
        if (summaryElement) {
          venueName = summaryElement.textContent?.trim() || '';
        }
        
        // Process each performance
        listElements.forEach(listItem => {
          if (listItem instanceof HTMLElement) {
            const timeText = listItem.textContent?.trim() || '';
            const artistElement = listItem.querySelector('strong');
            const artistText = artistElement ? artistElement.textContent?.trim() : '';
            
            if (timeText) {
              // Extract date and time information
              const { isPast, performanceDate } = isPerformanceInPast(timeText, today, dateFormat);
              
              // Store performance data
              items.push({
                element: listItem,
                time: timeText,
                venue: venueName,
                isPast,
                date: performanceDate?.toISOString()
              });
            }
          }
        });
      }
    });
    
    setPerformances(items);
    
    // Apply initial styling
    applyPerformanceStyling(items, showPastPerformances);
  };
  
  // Set up a mutation observer to watch for changes in the DOM
  useEffect(() => {
    // Start observing when the component mounts
    const targetNode = document.body;
    
    if (!targetNode) return;
    
    // Create a new observer instance
    observerRef.current = new MutationObserver((mutations) => {
      // Check if any of the mutations involve adding nodes that match our selectors
      const shouldUpdate = mutations.some(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes are or contain what we're looking for
          return Array.from(mutation.addedNodes).some(node => {
            if (node instanceof HTMLElement) {
              return node.matches(containerSelector) || 
                    node.querySelector(containerSelector) !== null;
            }
            return false;
          });
        }
        return false;
      });
      
      if (shouldUpdate) {
        // Reinitialize our performances when relevant DOM changes are detected
        initializePerformances();
      }
    });
    
    // Configuration of the observer
    const config = {
      childList: true, // Watch for additions/removals of child elements
      subtree: true,   // Watch all descendants, not just children
    };
    
    // Start observing
    observerRef.current.observe(targetNode, config);
    
    // Initialize immediately as well
    initializePerformances();
    
    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [containerSelector, venueSelector, timeSelector, dateFormat]);
  
  // Apply styling whenever showPastPerformances toggle changes
  useEffect(() => {
    applyPerformanceStyling(performances, showPastPerformances);
  }, [showPastPerformances, performances]);
  
  // Apply the grayed-out styling to past performances
  const applyPerformanceStyling = (items: PerformanceItem[], showPast: boolean) => {
    items.forEach(item => {
      if (item.isPast) {
        // Style for past performances
        if (showPast) {
          // Show past performances with gray styling
          item.element.style.opacity = '0.6';
          item.element.style.color = '#999';
          
          // Style the artist name to be gray as well
          const artistElement = item.element.querySelector('strong');
          if (artistElement) {
            artistElement.style.color = '#999';
          }
        } else {
          // Hide past performances completely
          item.element.style.display = 'none';
        }
      } else {
        // Reset styling for upcoming performances
        item.element.style.opacity = '1';
        item.element.style.color = '';
        
        // Reset artist name styling
        const artistElement = item.element.querySelector('strong');
        if (artistElement) {
          artistElement.style.color = '#0066cc'; // Restore original color
        }
        
        // Always show upcoming performances
        item.element.style.display = '';
      }
    });
  };
    // Determine if a performance is in the past based on the text
  const isPerformanceInPast = (
    timeText: string,
    currentDate: Date,
    format: 'US' | 'ISO' = 'US'
  ): { isPast: boolean; performanceDate?: Date } => {
    // Target date: June 7, 2025 (6/7/2025)
    const targetDate = new Date(2025, 5, 7); // Month is 0-indexed, so 5 = June
    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    // Before June 7, 2025: never gray (always false)
    if (currentDateOnly < targetDateOnly) {
      return { isPast: false, performanceDate: undefined };
    }
    
    // After June 7, 2025: always gray (always true)
    if (currentDateOnly > targetDateOnly) {
      return { isPast: true, performanceDate: undefined };
    }
    
    // On June 7, 2025: use time-based logic (existing implementation)
    // Default to assuming it's not a past performance if we can't parse the time
    let isPast = false;
    let performanceDate: Date | undefined;

    try {
      // Extract time information from text like "DJ Blkgas – 3:00 PM (and in between sets)"
      const timeMatch = timeText.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      
      if (timeMatch) {
        const hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const isPM = timeMatch[3].toUpperCase() === 'PM';
        
        // Create a date for the performance
        performanceDate = new Date(currentDate);
        
        // Set hours in 24-hour format
        let hour24 = hours;
        if (isPM && hours < 12) hour24 += 12;
        if (!isPM && hours === 12) hour24 = 0;
        
        performanceDate.setHours(hour24, minutes, 0, 0);
        
        // Look for date in the string (MM/DD or MM/DD/YYYY for US format or YYYY-MM-DD for ISO)
        const dateMatchUS = timeText.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?/);
        const dateMatchISO = timeText.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
        
        if (format === 'US' && dateMatchUS) {
          const month = parseInt(dateMatchUS[1]) - 1; // Months are 0-indexed in JS
          const day = parseInt(dateMatchUS[2]);
          const year = dateMatchUS[3] ? parseInt(dateMatchUS[3]) : currentDate.getFullYear();
          
          performanceDate.setFullYear(year, month, day);
        } else if (format === 'ISO' && dateMatchISO) {
          const year = parseInt(dateMatchISO[1]);
          const month = parseInt(dateMatchISO[2]) - 1; // Months are 0-indexed in JS
          const day = parseInt(dateMatchISO[3]);
          
          performanceDate.setFullYear(year, month, day);
        }

        // Compare with current date/time to determine if it's in the past
        isPast = performanceDate < currentDate;
      }
    } catch (e) {
      console.error('Error parsing performance time', e);
    }

    return { isPast, performanceDate };
  };

  // Handle toggle change
  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowPastPerformances(e.target.checked);
  };

  return (
    <Box width="100%" mb={2} px={2}>
      {/* <Flex 
        justify="center" 
        align="center" 
        gap={2} 
        minHeight="44px" // Mobile-friendly touch target size
      >
        <Text 
          fontSize="sm" 
          color="gray.600"
          fontStyle="italic"
        >
          Show past performances
        </Text>
        <Switch 
          isChecked={showPastPerformances} 
          onChange={handleToggleChange}
          colorScheme="green"
          size="md" // Increased size for better touch target
        />
      </Flex> */}
    </Box>
  );
};

export default PastPerformanceStyler;
